import { useContext } from 'react'

import { useForm, useLatestEntity } from '@domain-group/react-hooks'
import type { UseFormOptions } from '@domain-group/react-hooks'

import { launchModal } from '@domain-group/javascript-utils'

import useDispatch from '@hooks/useDispatch'
import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import * as favouriteOrderActions from '@redux/modules/favouriteOrder'

import defaultFormState, { requiredFields } from '@models/favouriteOrder'

import PageContext from '@contexts/pageContext'

import type { AppDispatch } from '@redux/store'
import type { DeleteParams, ModuleState } from '@redux/modules/types'
import type { FavouriteOrderModel, FavouriteOrderRequestOptions } from '@models/types'

export const generateUrls = (favouriteOrder?: Partial<FavouriteOrderModel>) => {
  const { id } = favouriteOrder || {}

  return {
    editFavouriteOrderDetailsUrl: `#/systemManager/favouriteOrders/${id}/edit/details`,
    editFavouriteOrderUrl: `#/systemManager/favouriteOrders/${id}/edit/`,
    favouriteOrdersIndexUrl: '#/systemManager/favouriteOrders',
  }
}

type CreateFavouriteOrderParams = {
  dispatch: AppDispatch,
  favouriteOrderParams: Partial<FavouriteOrderModel>,
  requestOptions?: FavouriteOrderRequestOptions,
}

const createFavouriteOrder = (params: CreateFavouriteOrderParams) => {
  const { dispatch, favouriteOrderParams, requestOptions } = params
  const { createFavouriteOrder: createFn } = favouriteOrderActions

  return dispatch(createFn(favouriteOrderParams, requestOptions))
}

type CreateOrderParams = {
  campaignId: number,
  favouriteOrderId: number,
}

type CreateOrderFromFavouriteParams = {
  dispatch: AppDispatch,
  createOrderParams: CreateOrderParams,
  requestOptions?: FavouriteOrderRequestOptions,
}

const createOrderFromFavourite = (params: CreateOrderFromFavouriteParams) => {
  const { dispatch, createOrderParams, requestOptions } = params
  const { createOrderFromFavourite: createFn } = favouriteOrderActions

  const { campaignId, favouriteOrderId } = createOrderParams

  return dispatch(createFn(favouriteOrderId, campaignId, requestOptions))
}

type UpdateFavouriteOrderParams = {
  dispatch: AppDispatch,
  favouriteOrder: FavouriteOrderModel,
  favouriteOrderParams: Partial<FavouriteOrderModel>,
  requestOptions?: FavouriteOrderRequestOptions,
}

const updateFavouriteOrder = (params: UpdateFavouriteOrderParams) => {
  const {
    dispatch, favouriteOrder, favouriteOrderParams, requestOptions,
  } = params
  const { updateFavouriteOrder: updateFn } = favouriteOrderActions

  const updatedParams = {
    id: favouriteOrder.id,
    ...favouriteOrderParams,
  }

  return dispatch(updateFn(updatedParams, requestOptions))
}

type DeleteFavouriteOrderParams = {
  dispatch: AppDispatch,
  favouriteOrder: DeleteParams<FavouriteOrderModel>,
}

const deleteFavouriteOrder = (params: DeleteFavouriteOrderParams) => {
  const { dispatch, favouriteOrder } = params
  const { deleteFavouriteOrder: deleteFn } = favouriteOrderActions

  return dispatch(deleteFn(favouriteOrder))
}

type CustomFormOptions = {
  customRequiredFields?: UseFormOptions['requiredFields'],
}

export function useFavouriteOrderForm(
  favouriteOrder: Partial<FavouriteOrderModel>,
  options: UseFormOptions & CustomFormOptions = {},
) {
  const { customRequiredFields = [], validateOn } = options || {}

  const favouriteOrderForm = useForm(
    defaultFormState,
    { entity: favouriteOrder, requiredFields: [...requiredFields, ...customRequiredFields], validateOn },
    [favouriteOrder.id, favouriteOrder.cache_key],
  )

  return {
    ...favouriteOrderForm,
  }
}

export const useRelations = (favouriteOrder: Partial<FavouriteOrderModel> = {}) => {
  const { agency_id, provider_id, publication_id } = favouriteOrder

  const entities = useSelector(reduxState => reduxState.entities)
  const { agencies, providers, publications } = entities

  const agency = agency_id && agencies[agency_id] ? agencies[agency_id] : {}
  const provider = provider_id && providers[provider_id] ? providers[provider_id] : {}
  const publication = publication_id && publications[publication_id] ? publications[publication_id] : {}

  return {
    agency,
    provider,
    publication,
  }
}

type Options = {
  performHttpRequests?: boolean,
  requestOptions?: FavouriteOrderRequestOptions,
}

function useFavouriteOrder(initEntity: Partial<FavouriteOrderModel> = {}, options: Options = {}) {
  const { performHttpRequests, requestOptions } = options

  // @ts-expect-error TS2322
  const { entity: favouriteOrder }: { entity: FavouriteOrderModel} = useLatestEntity(initEntity, 'favouriteOrders')
  const { id } = favouriteOrder

  const dispatch = useDispatch()

  const { callbacks } = useContext(PageContext)

  const { agency } = useRelations(favouriteOrder)

  useReduxAction(
    'favouriteOrders',
    'loadFavouriteOrder',
    {
      entityId: id,
      ...requestOptions,
    },
    [id, performHttpRequests],
    {
      dispatchAction: (action, actionRequestOptions) => action(id, actionRequestOptions),
      shouldPerformFn: ({ loading }: ModuleState) => !!performHttpRequests && !loading,
    },
  )

  const {
    creating, deleting, loadedIds, loading, updating,
  } = useSelector(reduxState => reduxState.favouriteOrders)

  return {
    agency,
    callbacks: {
      createFavouriteOrder: (
        favouriteOrderParams: Partial<FavouriteOrderModel>,
        entityOptions?: FavouriteOrderRequestOptions,
      ) => (
        createFavouriteOrder({ favouriteOrderParams, dispatch, requestOptions: entityOptions })
      ),
      createOrderFromFavourite: (
        createOrderParams: CreateOrderParams,
        entityOptions?: FavouriteOrderRequestOptions,
      ) => (
        createOrderFromFavourite({ createOrderParams, dispatch, requestOptions: entityOptions })
      ),
      createOrEditFavouriteOrder: () => launchModal({
        callbacks,
        modalKey: 'CreateOrEditFavouriteOrderModal',
        payload: { favouriteOrder },
      }),
      deleteFavouriteOrder: () => deleteFavouriteOrder({ dispatch, favouriteOrder }),
      updateFavouriteOrder: (
        favouriteOrderParams: Partial<FavouriteOrderModel>,
        entityOptions?: FavouriteOrderRequestOptions,
      ) => (
        updateFavouriteOrder({
          favouriteOrder, favouriteOrderParams, dispatch, requestOptions: entityOptions,
        })
      ),
    },
    creating,
    deleting,
    favouriteOrder,
    loading: loading && !loadedIds.includes(favouriteOrder.id),
    updating,
    urls: generateUrls(favouriteOrder),
  }
}

export default useFavouriteOrder
